import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Scale, HeartHandshake, Users, GraduationCap, Ear, Compass, HandHeart, Activity } from 'lucide-react';
import { useLang, ui } from '../i18n/LanguageContext';
import { categories, services } from '../mock/data';
import useReveal from '../hooks/useReveal';

const catIcons = {
  juridique: Scale,
  mediation: HeartHandshake,
  psychosocial: Users,
  ecole: GraduationCap,
  'sante-physique': Activity,
};

const Home = () => {
  const { t } = useLang();
  useReveal();

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center bg-[#161514] overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_transition-familiale/artifacts/qbvpel7e_20240106_CEF_226.webp"
            alt=""
            className="w-full h-full object-cover object-center grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#161514] via-[#161514]/70 to-transparent" />
        </div>

        <div className="relative max-w-[1280px] mx-auto px-5 md:px-8 w-full pt-28 pb-20">
          <div className="max-w-2xl">
            <p className="eyebrow text-champagne mb-6 fade-in-up">{t(ui.home.heroTag)}</p>
            <h1 className="font-serif text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] fade-in-up">
              {t(ui.home.heroTitle)}
            </h1>
            <p className="text-white/70 text-lg mt-8 leading-relaxed max-w-xl fade-in-up">
              {t(ui.home.heroSub)}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10 fade-in-up">
              <Link to="/services" className="group inline-flex items-center justify-center gap-2 bg-white text-[#161514] px-8 py-4 text-sm tracking-wide hover:bg-champagne hover:text-white transition-all duration-300">
                {t(ui.cta.discover)}
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-8 py-4 text-sm tracking-wide hover:bg-white/10 transition-all duration-300">
                {t(ui.cta.contact)}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#FBFAF8] border-b border-border">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-14 grid grid-cols-3 gap-6">
          {[
            { n: '3000+', l: t(ui.home.statsFamilies) },
            { n: '2016', l: t(ui.home.statsYears) },
            { n: '18', l: t(ui.home.statsServices) },
          ].map((s, i) => (
            <div key={i} className="text-center reveal" style={{ transitionDelay: `${i * 120}ms` }}>
              <p className="font-serif text-4xl md:text-6xl text-[#161514]">{s.n}</p>
              <p className="eyebrow text-champagne mt-3">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-16 reveal">
            <p className="eyebrow text-champagne mb-4">{t(ui.home.servicesTag)}</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">{t(ui.home.servicesTitle)}</h2>
            <p className="text-muted-foreground mt-6 text-lg leading-relaxed">{t(ui.home.servicesIntro)}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
            {categories.map((cat, i) => {
              const Icon = catIcons[cat.slug] || Activity;
              return (
                <Link
                  key={cat.slug}
                  to={cat.path || `/services?cat=${cat.slug}`}
                  className="group bg-[#FBFAF8] p-9 md:p-12 hover:bg-[#161514] transition-colors duration-500 reveal"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <Icon size={34} strokeWidth={1.3} className="text-champagne mb-8" />
                  <h3 className="font-serif text-2xl md:text-3xl mb-4 group-hover:text-white transition-colors">{t(cat.title)}</h3>
                  <p className="text-muted-foreground group-hover:text-white/60 transition-colors leading-relaxed">{t(cat.desc)}</p>
                  <span className="inline-flex items-center gap-2 mt-8 text-sm text-champagne">
                    {t(ui.cta.learnMore)} <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROACH split */}
      <section className="bg-secondary">
        <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-2">
          <div className="img-zoom h-72 lg:h-auto">
            <img
              src="https://images.unsplash.com/photo-1533659124865-d6072dc035e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Support"
              className="w-full h-full object-cover grayscale"
            />
          </div>
          <div className="px-6 md:px-16 py-16 md:py-24 flex flex-col justify-center reveal">
            <p className="eyebrow text-champagne mb-4">{t(ui.home.approachTag)}</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-8">{t(ui.home.approachTitle)}</h2>
            <div className="space-y-6">
              {[
                { icon: Ear, ...({}) },
                { icon: Compass },
                { icon: HandHeart },
              ].map((item, i) => {
                const texts = [
                  { fr: 'Nous vous \u00e9coutons sans jugement, \u00e0 l\u2019\u00e9coute de vos besoins r\u00e9els.', en: 'We listen to you without judgment, attentive to your real needs.' },
                  { fr: 'Nous vous guidons \u00e0 travers chaque \u00e9tape juridique et \u00e9motionnelle.', en: 'We guide you through each legal and emotional step.' },
                  { fr: 'Nous vous accompagnons vers un avenir plus serein.', en: 'We support you toward a more peaceful future.' },
                ];
                const Ic = item.icon;
                return (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="w-11 h-11 shrink-0 rounded-full border border-champagne flex items-center justify-center">
                      <Ic size={19} className="text-champagne" strokeWidth={1.4} />
                    </div>
                    <p className="text-[#4a4740] leading-relaxed pt-2">{t(texts[i])}</p>
                  </div>
                );
              })}
            </div>
            <Link to="/a-propos" className="inline-flex items-center gap-2 mt-10 text-sm link-underline w-fit">
              {t(ui.cta.learnMore)} <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-14 reveal">
            <div>
              <p className="eyebrow text-champagne mb-4">{t(ui.services.tag)}</p>
              <h2 className="font-serif text-3xl md:text-5xl">{t(ui.services.title)}</h2>
            </div>
            <Link to="/services" className="hidden md:inline-flex items-center gap-2 text-sm link-underline">
              {t(ui.cta.allServices)} <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((s, i) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group reveal hover-lift" style={{ transitionDelay: `${(i % 3) * 100}ms` }}>
                <div className="img-zoom aspect-[4/3] mb-5 bg-secondary">
                  <img src={s.image} alt={t(s.title)} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                </div>
                <h3 className="font-serif text-xl mb-2 group-hover:text-champagne transition-colors">{t(s.title)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(s.excerpt)}</p>
              </Link>
            ))}
          </div>
          <Link to="/services" className="md:hidden inline-flex items-center gap-2 text-sm link-underline mt-10">
            {t(ui.cta.allServices)} <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#161514] py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center reveal">
          <HandHeart size={40} strokeWidth={1.2} className="text-champagne mx-auto mb-8" />
          <h2 className="font-serif text-white text-3xl md:text-5xl leading-tight">{t(ui.home.ctaTitle)}</h2>
          <p className="text-white/60 mt-6 text-lg leading-relaxed">{t(ui.home.ctaSub)}</p>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-10 bg-white text-[#161514] px-9 py-4 text-sm tracking-wide hover:bg-champagne hover:text-white transition-all duration-300">
            {t(ui.cta.bookConsult)} <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
